import { type OrderWithRelations } from "@fitness-app/app-store";

export const INVOICE_ERROR_FILTER = "error";
export const EMPTY_FILTER_VALUE = "__none__";

type FilterValue = (boolean | React.Key)[];

export type OrderTableFilterKey =
  | "status"
  | "paymentType"
  | "orderType"
  | "checkoutVariantSlug"
  | "salesFunnelName"
  | "connectedInvoice";

export type OrderTableFilters = Partial<Record<OrderTableFilterKey, FilterValue | null>>;

type OrderFilterPredicate = (value: FilterValue[number], order: OrderWithRelations) => boolean;

const hasInvoiceError = (order: OrderWithRelations) =>
  Boolean(order.connectedInvoice && "errorMessage" in order.connectedInvoice);

const predicates: Record<OrderTableFilterKey, OrderFilterPredicate> = {
  status: (value, order) => order.status === value,
  paymentType: (value, order) =>
    Boolean(order.paymentType === value && !order.installments) || Boolean(order.installments && value === "installments"),
  orderType: (value, order) => {
    if (value === "new") {
      return Boolean((!order.metadata?.renewal && order.paymentCycle === 1) || !order.paymentCycle);
    }

    if (value === "renewal") {
      return Boolean(order.metadata?.renewal && order.paymentCycle === 1);
    }

    return Boolean(order.paymentCycle && order.paymentCycle > 1);
  },
  checkoutVariantSlug: (value, order) =>
    value === EMPTY_FILTER_VALUE ? !order.checkoutVariantSlug : order.checkoutVariantSlug === value,
  salesFunnelName: (value, order) =>
    value === EMPTY_FILTER_VALUE ? !order.salesFunnelName : order.salesFunnelName === value,
  connectedInvoice: (value, order) => {
    if (value === INVOICE_ERROR_FILTER) {
      return hasInvoiceError(order);
    }

    if (hasInvoiceError(order)) {
      return false;
    }

    return value === false ? !order.connectedInvoice : Boolean(order.connectedInvoice);
  },
};

const isFilterKey = (key: string): key is OrderTableFilterKey => key in predicates;

/**
 * Applies the orders table column filters outside of Ant Design, so the same
 * result can be fed both to the table and to the CSV export.
 *
 * Mirrors Ant Design semantics: a row has to match at least one selected value
 * within a column (OR), and every column with an active filter (AND).
 */
export function applyOrderFilters(orders: OrderWithRelations[], filters: OrderTableFilters): OrderWithRelations[] {
  const activeFilters = Object.entries(filters).filter(
    (entry): entry is [OrderTableFilterKey, FilterValue] => isFilterKey(entry[0]) && Boolean(entry[1]?.length),
  );

  if (!activeFilters.length) {
    return orders;
  }

  return orders.filter((order) =>
    activeFilters.every(([key, values]) => values.some((value) => predicates[key](value, order))),
  );
}
